import Dexie, { type EntityTable } from "dexie";
import type {
  AppSettings,
  AppUser,
  AuditLog,
  AuthSession,
  ImportBatch,
  InventoryBalance,
  InventoryMovement,
  MarketplaceListing,
  MarketplaceVariantListing,
  PendingSync,
  Product,
  ProductImportBatch,
  ProductVariant,
  VariantAlias,
} from "../types";
import { randomId } from "../utils/id";
import { resolveApiUrl } from "../services/runtimeConfig.mjs";

export interface KeyValue {
  key: string;
  value: unknown;
}

export class WGDatabase extends Dexie {
  products!: EntityTable<Product, "id">;
  variants!: EntityTable<ProductVariant, "id">;
  balances!: EntityTable<InventoryBalance, "id">;
  movements!: EntityTable<InventoryMovement, "id">;
  pendingSync!: EntityTable<PendingSync, "id">;
  imports!: EntityTable<ImportBatch, "id">;
  auditLogs!: EntityTable<AuditLog, "id">;
  settings!: EntityTable<AppSettings, "id">;
  users!: EntityTable<AppUser, "id">;
  sessions!: EntityTable<AuthSession, "id">;
  kv!: EntityTable<KeyValue, "key">;
  variantAliases!: EntityTable<VariantAlias, "id">;
  marketplaceListings!: EntityTable<MarketplaceListing, "id">;
  marketplaceVariants!: EntityTable<MarketplaceVariantListing, "id">;
  productImports!: EntityTable<ProductImportBatch, "id">;

  constructor() {
    super("wg_tricot_estoque");
    this.version(1).stores({
      products: "id, &sku, name, category, status, updatedAt",
      variants: "id, productId, colorName, status, malhariaEnabled, updatedAt",
      balances: "id, &[variantId+warehouse], variantId, warehouse, updatedAt",
      movements: "id, operationId, variantId, warehouse, type, createdAt, syncStatus",
      pendingSync: "id, operationType, status, createdAt, updatedAt",
      imports: "id, &fileHash, source, status, createdAt",
      auditLogs: "id, entityType, entityId, userId, createdAt",
      settings: "id",
      users: "id, &email, role, status",
      sessions: "id",
      kv: "key",
    });
    this.version(2).stores({
      products: "id, &sku, name, category, status, updatedAt",
      variants: "id, productId, colorName, status, malhariaEnabled, updatedAt",
      balances: "id, &[variantId+warehouse], variantId, warehouse, updatedAt",
      movements: "id, operationId, variantId, warehouse, type, createdAt, syncStatus",
      pendingSync: "id, operationType, status, createdAt, updatedAt",
      imports: "id, &fileHash, source, status, createdAt",
      auditLogs: "id, entityType, entityId, userId, createdAt",
      settings: "id",
      users: "id, &email, role, status",
      sessions: "id",
      kv: "key",
      variantAliases: "id, &[productId+normalizedAlias], productId, variantId, kind, normalizedAlias, updatedAt",
      marketplaceListings: "id, &[marketplace+externalListingId], productId, importId, marketplace, updatedAt",
      marketplaceVariants: "id, listingId, productId, variantId, externalVariantId, marketplaceSku, updatedAt",
      productImports: "id, &fileHash, source, status, createdAt",
    });
  }
}

export const db = new WGDatabase();

const now = () => new Date().toISOString();
const id = randomId;

export async function getDeviceId(): Promise<string> {
  const current = await db.kv.get("device_id");
  if (typeof current?.value === "string") return current.value;
  const deviceId = id("device");
  await db.kv.put({ key: "device_id", value: deviceId });
  return deviceId;
}

export async function initializeDatabase(): Promise<void> {
  const configuredApi = resolveApiUrl(import.meta.env.VITE_API_URL, window.location);
  const currentSettings = await db.settings.get("app");
  if (!currentSettings) {
    await db.settings.put({
      id: "app",
      companyName: "WG Tricot",
      allowNegativeStock: false,
      defaultMinimumStock: 10,
      theme: "sistema",
      apiUrl: configuredApi,
      demoData: false,
    });
  } else {
    const resolvedApi = resolveApiUrl(currentSettings.apiUrl || import.meta.env.VITE_API_URL, window.location);
    await db.settings.update("app", {
      apiUrl: resolvedApi,
      demoData: false,
    });
  }

  await getDeviceId();
  await purgeLegacyDemoData();
}

async function purgeLegacyDemoData(): Promise<void> {
  if (await db.kv.get("legacy_demo_purged_v140")) return;
  const productIds = ["prod-cm001", "prod-vt001", "prod-bc001"];
  const variantIds = ["var-cm-preto", "var-cm-areia", "var-cm-off", "var-vt-preto", "var-vt-verde", "var-bc-azul"];

  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.users, db.sessions, db.kv, db.settings, db.variantAliases, db.marketplaceListings, db.marketplaceVariants], async () => {
    await db.products.bulkDelete(productIds);
    await db.variants.bulkDelete(variantIds);
    await db.balances.where("variantId").anyOf(variantIds).delete();
    await db.movements.where("variantId").anyOf(variantIds).delete();
    await db.variantAliases.where("productId").anyOf(productIds).delete();
    await db.marketplaceListings.where("productId").anyOf(productIds).delete();
    await db.marketplaceVariants.where("productId").anyOf(productIds).delete();
    await db.users.delete("demo-admin");
    const session = await db.sessions.get("current");
    if (session?.user.id === "demo-admin") await db.sessions.delete("current");
    await db.settings.update("app", { demoData: false });
    await db.kv.put({ key: "legacy_demo_purged_v140", value: true });
    await db.kv.put({ key: "demo_removed", value: true });
  });
}
